import type { ComponentPropsOptions, PropType } from "vue";
import type { ThemeColor } from "@/colors";
import type { OptionLabel } from "@/components/helpers/types";
import { defineComponent } from "@vue/runtime-core";
import { CheckboxIndicator, CheckboxRoot } from "reka-ui";
import { computed } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { renderComponent } from "@/utils/render-component";
import { fieldsetCtx } from "../fieldset/Fieldset";
import Icon from "../icon/Icon";
import { checkboxLabelVariants, checkboxVariants } from "./variants";

const checkboxProps = {
  id: p.string(() => {
    const suffix = Math.random().toString(36).slice(2, 11);
    return "field-" + suffix;
  }),
  modelValue: p.custom<boolean | number>([Boolean, Number]),
  value: p.custom<string | number>([String, Number]),
  label: {
    type: [String, Function] as PropType<OptionLabel>,
    default: "",
  },
  ariaLabel: p.string(""),
  onChange: p.func<(e: boolean | number) => void>(),
  size: p.string<"sm" | "md" | "lg">("md"),
  disabled: p.boolean(),
  indeterminate: p.boolean(),
  color: p.string<ThemeColor>(),
  description: p.string(),
  descriptionClass: p.string(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  props: checkboxProps,
  emits: ["update:modelValue"],
  setup(props, { emit, attrs }) {
    const field = fieldsetCtx.inject({
      id: props.id,
      legend: "",
    });
    const type = typeof props.modelValue;
    const vModel = computed<boolean | number>({
      get() {
        return props.indeterminate || !!props.modelValue;
      },
      set(val: boolean | number) {
        emit("update:modelValue", type === "number" ? Number(val) : !!val);
        props.onChange?.(type === "number" ? Number(val) : !!val);
      },
    });

    return () => (
      <div class={cn("flex flex-row gap-3", attrs.class ?? "")}>
        <CheckboxRoot
          id={props.id || field.id}
          v-model={vModel.value}
          class={checkboxVariants({
            size: props.size,
          })}
          aria-label={props.ariaLabel || undefined}
          aria-disabled={props.disabled ? "true" : undefined}
          data-accent={props.color}
          disabled={props.disabled}
        >
          <CheckboxIndicator class={cn("flex items-center justify-center text-background")}>
            <Icon icon={props.indeterminate ? "lucide:minus" : "lucide:check"} class="size-3.5" />
          </CheckboxIndicator>
        </CheckboxRoot>
        <label
          v-show={!!props.label}
          for={props.id || field.id}
          class={cn(
            checkboxLabelVariants({
              size: props.size,
              disabled: props.disabled,
            }),
            "leading-none"
          )}
        >
          <span
            class={cn(
              "-mt-0.5 text-[13px] leading-normal text-foreground",
              props.description ? "font-bold" : ""
            )}
          >
            {renderComponent(props.label)}
          </span>
          {props.description && (
            <span class={cn("text-xs text-muted-foreground", props.descriptionClass)}>
              {props.description}
            </span>
          )}
        </label>
      </div>
    );
  },
});
